import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAuthToken } from '../utils/authUtils';
import { RedeemCouponSheet } from './RedeemCouponSheet';
import { RedeemInfoSheet, redeemRates } from './RedeemInfoSheet';
import { ReferralTracker } from './ReferralTracker';
import { useCountUp } from './useCountUp';
import './WalletPage.css';

const e = React.createElement;

const parseTxns = (data) =>
  Array.isArray(data) ? data : data.results ?? data.transactions ?? [];

const formatCoins = (n) =>
  typeof n === 'number' ? n.toLocaleString('en-IN') : n;

// A coupon link (egadmin's Copy link, /wallet?coupon=CODE) opens the redeem
// sheet with its code filled in. Matches WALLET_COUPON_PARAM on the backend.
const COUPON_PARAM = 'coupon';

// URLSearchParams rather than the shared getQueryVariable: its
// decodeURIComponent throws on a malformed link (?coupon=%E0), and this
// runs during render.
const couponFromUrl = () =>
  new URLSearchParams(window.location.search).get(COUPON_PARAM)?.trim()
  || null;

// Taken off the URL once used, so a refresh or a shared copy of the address
// does not open the sheet again.
const dropCouponFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  params.delete(COUPON_PARAM);
  const search = params.toString() ? `?${params}` : '';
  const { pathname, hash } = window.location;
  window.history.replaceState(
    window.history.state, '', `${pathname}${search}${hash}`
  );
};

/**
 * Factory that creates a WalletPage component so the same
 * implementation can be reused across all apps.
 *
 * @param {Object} config
 * @param {Function} config.getAPIUrl - App-specific URL builder
 * @param {React.ComponentType} config.Navbar - App's Navbar component
 * @param {Function} [config.renderBetween] - App-specific tile, rendered
 *   as the second cell of the actions grid beside the redeem tile. Built
 *   to the same anatomy and metrics, or the pair stops looking like a pair.
 * @param {Function} [config.renderBeforeReferrals] - App-specific block,
 *   rendered full width directly above the referral tracker.
 */
export const createWalletPage = ({
  getAPIUrl, Navbar, renderBetween, renderBeforeReferrals,
}) => {
  const WalletPage = () => {
    const [wallet, setWallet] = useState(null);
    const txnSectionRef = useRef(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [txnLoading, setTxnLoading] = useState(false);
    const [showViewAll, setShowViewAll] = useState(false);
    const [error, setError] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetCode, setSheetCode] = useState('');
    // Read once, on the first render.
    const linkedCode = useRef(couponFromUrl());
    const [infoOpen, setInfoOpen] = useState(false);

    const balanceShown = useCountUp(wallet?.balance_coins);

    // Live from the wallet API, so an ops reprice shows without a release.
    // Same reading the sheet quotes, so the chip cannot contradict it.
    const { booking: bookingRate } = redeemRates(wallet?.redeem_rates);

    // The conversion rate is a fact about the coin, not about this balance, so
    // it sits in the card's corner as a chip instead of a line under the number.
    const rateChip = e(
      'div',
      {
        className: 'wallet-rate-chip',
        title: `1 EG Coin = ₹${bookingRate} if used on EG platform`,
      },
      e('span', { className: 'wallet-rate-chip__coin', 'aria-hidden': 'true' }, 'EG'),
      e('span', { className: 'wallet-rate-chip__text' }, '1 EG'),
      e('span', { className: 'wallet-rate-chip__eq' }, '='),
      e('span', { className: 'wallet-rate-chip__rupee' }, `₹ ${bookingRate}`)
    );

    // Balance and transactions are read together: a redemption writes both, so
    // refreshing one alone shows a balance the list cannot account for.
    const loadWallet = useCallback(async () => {
      const headers = { Authorization: getAuthToken() };
      const [walletRes, txnRes] = await Promise.all([
        fetch(getAPIUrl('/api/payment/v2/wallet/'), { method: 'GET', headers }),
        fetch(getAPIUrl('/api/payment/v2/wallet/transactions'), { method: 'GET', headers }),
      ]);
      const walletData = await walletRes.json();
      const txnData = await txnRes.json();
      setWallet(walletData);
      setTransactions(parseTxns(txnData));
      setShowViewAll(!!txnData.show_view_all);
    }, []);

    useEffect(() => {
      const fetchWallet = async () => {
        setLoading(true);
        try {
          await loadWallet();
        } catch (err) {
          setError('Failed to load wallet.');
        } finally {
          setLoading(false);
        }
      };
      fetchWallet();
    }, [loadWallet]);

    // Waits for the wallet so a blocked one is never offered the sheet, and
    // clears the code as it goes: the wallet reloads after a redeem, which
    // would otherwise open the sheet a second time.
    useEffect(() => {
      const code = linkedCode.current;
      if (!code || loading || !wallet) return;
      linkedCode.current = null;
      dropCouponFromUrl();
      if (wallet.is_blocked) return;
      setSheetCode(code);
      setSheetOpen(true);
    }, [loading, wallet]);

    const handleViewAll = async () => {
      setTxnLoading(true);
      try {
        const res = await fetch(
          getAPIUrl('/api/payment/v2/wallet/transactions?view_all=true'),
          { method: 'GET', headers: { Authorization: getAuthToken() } }
        );
        const data = await res.json();
        setTransactions(parseTxns(data));
        setShowViewAll(false);
      } catch (err) {
        // keep existing transactions on failure
      } finally {
        setTxnLoading(false);
      }
    };

    if (loading) {
      return e(
        React.Fragment,
        null,
        Navbar ? e(Navbar, { title: 'Wallet' }) : null,
        e(
          'div',
          { className: 'wallet-page' },
          e(
            'div',
            { className: 'wallet-balance-card' },
            rateChip,
            e('p', { className: 'wallet-label' }, 'EG Coins Balance'),
            e(
              'div',
              { className: 'wallet-balance-amount', style: { opacity: 0.4 } },
              e('div', { className: 'wallet-coin-badge' }, 'EG'),
              e('span', null, '—')
            ),
            e('div', { className: 'wallet-pedestal' })
          ),
          e(
            'div',
            { className: 'wallet-txn-section' },
            e(
              'div',
              { className: 'wallet-txn-header' },
              'Recent Transactions'
            ),
            e(
              'div',
              { className: 'wallet-empty-state' },
              e('p', null, 'Loading...')
            )
          )
        )
      );
    }

    if (error) {
      return e(
        React.Fragment,
        null,
        Navbar ? e(Navbar, { title: 'Wallet' }) : null,
        e(
          'div',
          { className: 'wallet-page' },
          e(
            'div',
            { className: 'wallet-txn-section' },
            e(
              'div',
              { className: 'wallet-empty-state' },
              e('div', { className: 'wallet-empty-icon' }, '⚠️'),
              e('p', { style: { color: '#fc8181' } }, error)
            )
          )
        )
      );
    }

    const isBlocked = wallet?.is_blocked;
    const lifetimeEarnings = wallet?.lifetime_earnings_coins ?? wallet?.lifetime_earnings ?? null;

    const formatTxnType = (type) => {
      if (!type) return 'Transaction';
      return type.replace(/^(credit|debit)_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    const txnItems = transactions.length === 0
      ? e(
          'div',
          { className: 'wallet-empty-state' },
          e('div', { className: 'wallet-empty-icon' }, '🌕'),
          e('p', null, 'No transactions yet'),
          e('p', { style: { fontSize: '0.8rem' } }, 'Your coin activity will appear here')
        )
      : transactions.map((txn, i) => {
          const isCredit = txn.direction === 'credit';
          const isExpired = !!txn.is_expired;
          return e(
            'div',
            { className: `wallet-txn-item${isExpired ? ' expired' : ''}`, key: txn.id ?? i },
            e(
              'div',
              { className: 'wallet-txn-left' },
              e(
                'div',
                { className: `wallet-txn-icon ${isCredit ? 'credit' : 'debit'}` },
                isCredit ? '↓' : '↑'
              ),
              e(
                'div',
                { style: { minWidth: 0, textAlign: 'left' } },
                e(
                  'div',
                  { className: 'wallet-txn-desc' },
                  txn.notes || formatTxnType(txn.txn_type)
                ),
                txn.created_display
                  ? e('div', { className: 'wallet-txn-date' }, txn.created_display)
                  : null,
                txn.expires_at
                  ? e('div', { className: isExpired ? 'wallet-txn-expiry-date' : 'wallet-txn-date' }, `${isExpired ? 'Expired' : 'Expires'}: ${txn.expires_at}`)
                  : null
              )
            ),
            e(
              'div',
              { className: 'wallet-txn-right' },
              e(
                'div',
                { className: `wallet-txn-amount ${isCredit ? 'credit' : 'debit'}` },
                `${isCredit ? '+' : '-'}${txn.amount_coins}`
              )
            )
          );
        });

    const waveLines = e(
      'svg',
      {
        style: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' },
        viewBox: '0 0 400 280',
        preserveAspectRatio: 'xMidYMid slice',
        'aria-hidden': 'true',
      },
      e('path', { d: 'M-30 100 C60 50,140 150,240 100 S380 50,430 100', stroke: 'rgba(255,255,255,0.10)', fill: 'none', strokeWidth: '1.5' }),
      e('path', { d: 'M-30 120 C60 70,140 170,240 120 S380 70,430 120', stroke: 'rgba(255,255,255,0.08)', fill: 'none', strokeWidth: '1.5' }),
      e('path', { d: 'M-30 140 C60 90,140 190,240 140 S380 90,430 140', stroke: 'rgba(255,255,255,0.08)', fill: 'none', strokeWidth: '1.5' }),
      e('path', { d: 'M-30 160 C60 110,140 210,240 160 S380 110,430 160', stroke: 'rgba(255,255,255,0.06)', fill: 'none', strokeWidth: '1' }),
      e('path', { d: 'M-30 80 C60 30,140 130,240 80 S380 30,430 80', stroke: 'rgba(255,255,255,0.06)', fill: 'none', strokeWidth: '1' }),
      e('path', { d: 'M-30 180 C60 130,140 230,240 180 S380 130,430 180', stroke: 'rgba(255,255,255,0.04)', fill: 'none', strokeWidth: '0.8' }),
      e('path', { d: 'M-30 60 C60 10,140 110,240 60 S380 10,430 60', stroke: 'rgba(255,255,255,0.04)', fill: 'none', strokeWidth: '0.8' }),
      e('path', { d: 'M-30 200 C60 150,140 250,240 200 S380 150,430 200', stroke: 'rgba(255,255,255,0.03)', fill: 'none', strokeWidth: '0.8' }),
      e('path', { d: 'M-30 220 C60 170,140 270,240 220 S380 170,430 220', stroke: 'rgba(255,255,255,0.02)', fill: 'none', strokeWidth: '0.8' }),
    );

    return e(
      React.Fragment,
      null,
      Navbar ? e(Navbar, { title: 'Wallet' }) : null,
      e(
        'div',
        { className: 'wallet-page' },
        e(
          'div',
          { className: 'wallet-balance-card' },
          waveLines,
          rateChip,
          e('p', { className: 'wallet-label' }, 'EG Coins Balance'),
          e(
            'div',
            { className: 'wallet-balance-amount' },
            e('div', { className: 'wallet-coin-badge' }, 'EG'),
            e('span', { style: { fontWeight: 900 } }, formatCoins(balanceShown))
          ),
          wallet.expires_at
            ? e(
                'div',
                { className: 'wallet-expires' },
                `Expires: ${wallet.expires_at}`
              )
            : null,
          // The list sits below the redeem and referral blocks, out of
          // sight on a phone, so the card offers a way straight to it.
          e(
            'button',
            {
              type: 'button',
              className: 'wallet-txn-jump',
              onClick: () => txnSectionRef.current?.scrollIntoView({
                behavior: 'smooth', block: 'start',
              }),
            },
            'View transactions'
          ),
          e('div', { className: 'wallet-pedestal' })
        ),
        e(
          'button',
          {
            type: 'button',
            className: 'wallet-redeem-info-link',
            onClick: () => setInfoOpen(true),
          },
          'How to redeem EG Coins?'
        ),
        // Lifetime earnings and wallet state share one row. They are a
        // fact each, and as two full-width bands they pushed everything
        // worth acting on below the fold.
        e(
          'div',
          { className: 'wallet-meta-row' },
          lifetimeEarnings != null
            ? e(
                'div',
                { className: 'wallet-lifetime' },
                e('span', { className: 'wallet-lifetime-star' }, '✦'),
                `Lifetime : ${formatCoins(lifetimeEarnings)}`
              )
            : null,
          e(
            'div',
            { className: 'wallet-status-row' },
            e('span', { className: `wallet-status-dot ${isBlocked ? 'blocked' : ''}` }),
            e('span', null, isBlocked ? 'Wallet Blocked' : 'Active')
          )
        ),
        // Spend and earn as one row of two, rather than two stacked
        // banners the width of the page.
        e(
          'div',
          { className: 'wallet-actions' },
          e(
            'button',
            {
              className: 'wallet-redeem-tile',
              type: 'button',
              onClick: () => setSheetOpen(true),
              disabled: isBlocked,
            },
            e(
              'span',
              { className: 'wallet-redeem-left' },
              e('span', { className: 'wallet-redeem-icon', 'aria-hidden': 'true' }, '🎟️'),
              e(
                'span',
                { className: 'wallet-redeem-text' },
                e('span', { className: 'wallet-redeem-title' }, 'Coupon Code?'),
                e(
                  'span',
                  { className: 'wallet-redeem-desc' },
                  isBlocked
                    ? 'Wallet blocked, coupons cannot be redeemed'
                    : 'Redeem it for EG Coins'
                )
              )
            ),
            e('span', { className: 'wallet-redeem-cta' }, 'Redeem →')
          ),
          renderBetween ? renderBetween(wallet) : null
        ),
        renderBeforeReferrals ? renderBeforeReferrals(wallet) : null,
        // Below the refer-and-earn block on purpose: the invitation comes
        // first, and what came of the last one reads as the answer to it.
        e(ReferralTracker, { getAPIUrl }),
        e(
          'div',
          { className: 'wallet-txn-section', ref: txnSectionRef },
          e(
            'div',
            { className: 'wallet-txn-header' },
            'Recent Transactions'
          ),
          txnItems,
          showViewAll
            ? e(
                'button',
                {
                  className: 'wallet-view-all-btn',
                  onClick: handleViewAll,
                  disabled: txnLoading,
                },
                txnLoading ? 'Loading...' : 'View All Transactions'
              )
            : null
        )
      ),
      e(RedeemCouponSheet, {
        open: sheetOpen,
        // The linked code is for the first open only.
        onClose: () => {
          setSheetOpen(false);
          setSheetCode('');
        },
        // The sheet reports the new balance itself, so a failed refresh only
        // leaves the page behind it stale until the next load.
        onRedeemed: () => loadWallet().catch(() => {}),
        getAPIUrl,
        initialCode: sheetCode,
      }),
      e(RedeemInfoSheet, {
        open: infoOpen,
        onClose: () => setInfoOpen(false),
        balance: wallet.balance_coins,
        giftCardCoins: wallet.gift_card_coins,
        rates: wallet.redeem_rates,
        limits: wallet.redeem_limits,
      })
    );
  };

  return WalletPage;
};
